/* Co-located React context + modal; hook is intentionally exported with the provider. */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertCircle, LayoutGrid } from 'lucide-react';
import type { ImpliedConnectionRow } from './strategyCanvasCreatePreview';

export type CreateConfirmDecision = 'proceed' | 'components' | 'cancel';

export interface CreateConfirmRequest {
  title: string;
  summary: string;
  rows: ImpliedConnectionRow[];
}

interface ConfirmContextValue {
  requestConfirm: (req: CreateConfirmRequest) => Promise<CreateConfirmDecision>;
}

const StrategyCanvasCreateConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useStrategyCanvasCreateConfirmOptional(): ConfirmContextValue | null {
  return useContext(StrategyCanvasCreateConfirmContext);
}

export function StrategyCanvasCreateConfirmProvider({
  children,
  onSwitchToComponents,
}: {
  children: ReactNode;
  onSwitchToComponents: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CreateConfirmRequest | null>(null);
  const resolverRef = useRef<((v: CreateConfirmDecision) => void) | null>(null);

  const requestConfirm = useCallback((req: CreateConfirmRequest) => {
    return new Promise<CreateConfirmDecision>((resolve) => {
      resolverRef.current = resolve;
      setRequest(req);
      setOpen(true);
    });
  }, []);

  const finish = useCallback((decision: CreateConfirmDecision) => {
    setOpen(false);
    setRequest(null);
    resolverRef.current?.(decision);
    resolverRef.current = null;
  }, []);

  const handleComponents = useCallback(() => {
    onSwitchToComponents();
    finish('components');
  }, [onSwitchToComponents, finish]);

  return (
    <StrategyCanvasCreateConfirmContext.Provider value={{ requestConfirm }}>
      {children}
      {open && request && (
        <div
          className="fixed inset-0 z-[240] flex items-center justify-center p-4 bg-black/50 dark:bg-black/60"
          role="dialog"
          aria-modal="true"
          aria-labelledby="canvas-create-confirm-title"
        >
          <div className="w-full max-w-md border border-[var(--rule)] bg-[var(--bg-raised)] shadow-xl">
            <div className="flex items-start gap-3 px-5 pt-5 pb-2">
              <div className="flex-shrink-0 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 p-2">
                <AlertCircle className="h-5 w-5 text-[var(--mustard)] dark:text-[var(--mustard)]" />
              </div>
              <div className="min-w-0 flex-1">
                <h2
                  id="canvas-create-confirm-title"
                  className="text-base font-semibold text-[var(--text-pri)]"
                >
                  {request.title}
                </h2>
                <p className="mt-1 text-sm text-[var(--text-sec)] leading-relaxed">
                  {request.summary}
                </p>
              </div>
              <button
                type="button"
                onClick={() => finish('cancel')}
                className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
                aria-label="Close"
              >
                <span className="sr-only">Close</span>
                ×
              </button>
            </div>

            <div className="px-5 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] mb-2">
                What you&apos;re adding together
              </p>
              <ul className="space-y-3 max-h-52 overflow-y-auto pr-1">
                {request.rows.map((row, i) => (
                  <li
                    key={`${row.kind}-${i}`}
                    className="border border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg-raised)]/40 px-3 py-2"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                      {row.kind}
                    </p>
                    <p className="text-sm font-medium text-[var(--text-pri)] mt-0.5">
                      {row.label}
                    </p>
                    {row.detail && (
                      <p className="text-xs text-[var(--text-sec)] mt-1 leading-relaxed">
                        {row.detail}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--bg-raised)]/30">
              <button
                type="button"
                onClick={() => finish('cancel')}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleComponents}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium border border-[var(--rule)] text-[var(--text-pri)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
              >
                <LayoutGrid className="h-4 w-4" />
                Adjust in Components view
              </button>
              <button
                type="button"
                onClick={() => finish('proceed')}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] dark:bg-[var(--slate)] dark:hover:bg-[var(--slate)]"
              >
                Create &amp; apply links
              </button>
            </div>
          </div>
        </div>
      )}
    </StrategyCanvasCreateConfirmContext.Provider>
  );
}
